import moment from "moment";

/** Parse a weight-table row date (ISO diaryDate, or legacy DD/MM label). */
export function parseDiaryDate(item) {
	if (item && item.diaryDate) {
		const iso = moment(item.diaryDate, "YYYY-MM-DD");
		if (iso.isValid()) {
			return iso.toDate();
		}
	}
	if (item && item.diaryDateFormated) {
		const dmy = moment(item.diaryDateFormated, "DD/MM");
		if (dmy.isValid()) {
			return dmy.toDate();
		}
	}
	return new Date();
}

export function findTableRowForDate(rows, calendarDate) {
	if (!Array.isArray(rows) || !calendarDate) {
		return undefined;
	}
	const iso = moment(calendarDate).format("YYYY-MM-DD");
	const dmy = moment(calendarDate).format("DD/MM");
	return rows.find(
		(row) =>
			row.diaryDate === iso ||
			row.diaryDateFormated === dmy ||
			row.diaryDateFormated === iso
	);
}

export function weightDiaryDisplayDate(item) {
	if (!item) {
		return "";
	}
	return item.diaryDateLabel || item.diaryDateFormated || "";
}

export function diaryRowsHaveRounds(rows) {
	return Array.isArray(rows)
		&& rows.length > 0
		&& rows.every((row) => typeof row.roundIndex === "number");
}

/** Keeps API order. Rows without roundIndex stay in one group. */
export function groupDiaryRounds(rows) {
	const groups = [];
	(rows || []).forEach((row) => {
		const roundIndex = row.roundIndex ?? 0;
		const current = groups[groups.length - 1];
		if (!current || current.roundIndex !== roundIndex) {
			groups.push({roundIndex, rows: [row]});
		} else {
			current.rows.push(row);
		}
	});
	return groups;
}

function withMorningWeight(rows) {
	return rows.filter((row) => {
		const weight = Number(row.morningWeight);
		return row.morningWeight != null && row.morningWeight !== "" && !Number.isNaN(weight);
	});
}

/**
 * The morning Diet finished: first Unloading, otherwise Stabilization, otherwise Finished.
 * If Diet never finished, the last Diet morning. Null when the round never entered Diet.
 */
export function dietResultMorning(rows) {
	const weighed = withMorningWeight(rows);
	const unloading = weighed.find((row) => row.phase === "UNLOADING");
	if (unloading) {
		return unloading;
	}
	const stabilization = weighed.find((row) => row.phase === "STABILIZATION");
	if (stabilization) {
		return stabilization;
	}
	const finished = weighed.find((row) => row.phase === "FINISHED");
	if (finished) {
		return finished;
	}
	const diet = weighed.filter((row) => row.phase === "DIET");
	if (diet.length > 0) {
		return diet[diet.length - 1];
	}
	return null;
}

function roundKg(value) {
	return Math.round(Number(value) * 10) / 10;
}

export function formatKgAmount(kg) {
	const text = roundKg(Math.abs(kg)).toFixed(1);
	return text.endsWith(".0") ? text.slice(0, -2) : text;
}

export function summarizeRound(rows) {
	const weighed = withMorningWeight(rows);
	if (weighed.length === 0) {
		return null;
	}
	const start = weighed[0];
	const result = dietResultMorning(rows);
	const end = result || weighed[weighed.length - 1];
	const startWeight = Number(start.morningWeight);
	const endWeight = Number(end.morningWeight);
	return {
		startDate: weightDiaryDisplayDate(start),
		startWeight,
		endDate: weightDiaryDisplayDate(end),
		endWeight,
		loss: result == null ? null : roundKg(endWeight - startWeight),
	};
}

export function roundResultKind(lossKg) {
	const rounded = roundKg(lossKg);
	if (rounded < 0) {
		return "lost";
	}
	if (rounded > 0) {
		return "gained";
	}
	return "same";
}

/** Shows "80 kg", and keeps the number with the unit when the line wraps. */
export function keepUnitTogether(text) {
	return String(text).replace(/[\u00A0 ](kg|кг|ק״ג|ק"ג)/g, "\u00A0$1");
}

/** One centered sentence for a closed round. */
export function roundStory(summary, t) {
	const shared = {
		startDate: summary.startDate,
		endDate: summary.endDate,
		startWeight: formatKgAmount(summary.startWeight),
		endWeight: formatKgAmount(summary.endWeight),
	};
	if (summary.loss == null) {
		return keepUnitTogether(t("round_summary_open", {...shared, interpolation: {escapeValue: false}}));
	}
	const kind = roundResultKind(summary.loss);
	const result = kind === "same"
		? t("round_same")
		: t(kind === "lost" ? "round_lost" : "round_gained", {
			amount: formatKgAmount(summary.loss),
		});
	return keepUnitTogether(t("round_summary", {...shared, result, interpolation: {escapeValue: false}}));
}
